import { Box, CircularProgress, Typography } from '@mui/material'
import { useQuery } from '@tanstack/react-query'

import { ObstructionReport, Trail } from 'models/Trail.model'
import { TrailMapResponse } from 'models/TrailMapResponse.model'
import { FC, useEffect, useRef, useState } from 'react'

import { ApiQueryService } from '../../services'

declare const L: any

// Nearest point on segment a→b to point p, all as [lat, lng]
function nearestPointOnSegment(p: [number, number], a: [number, number], b: [number, number]): [number, number] {
  const dy = b[0] - a[0],
    dx = b[1] - a[1]
  const d2 = dy * dy + dx * dx
  if (d2 === 0) return a
  const t = Math.max(0, Math.min(1, ((p[0] - a[0]) * dy + (p[1] - a[1]) * dx) / d2))
  return [a[0] + t * dy, a[1] + t * dx]
}

// Returns the nearest trail point and its distance in metres, or null if no polylines
function snapToTrail(
  lat: number,
  lng: number,
  polylines: [number, number][][],
): { point: [number, number]; dist: number } | null {
  let best: [number, number] | null = null
  let bestDist = Infinity
  for (const line of polylines) {
    for (let i = 0; i < line.length - 1; i++) {
      const candidate = nearestPointOnSegment([lat, lng], line[i], line[i + 1])
      const dist = L.latLng(lat, lng).distanceTo(L.latLng(candidate[0], candidate[1]))
      if (dist < bestDist) {
        bestDist = dist
        best = candidate
      }
    }
  }
  return best ? { point: best, dist: bestDist } : null
}

type TrailMapProps = {
  trailId: number
  trail?: Trail
  onLocationSelect?: (lat: number, lng: number) => void
  height?: number
  initialCenter?: [number, number]
  initialZoom?: number
}
export const TrailMap: FC<TrailMapProps> = (props) => {
  type LatLng = [number, number]
  const [wayPolylines, setWayPolylines] = useState<LatLng[][]>([])
  const [mapError, setMapError] = useState<boolean>(false)
  const [mapInitialized, setMapInitialized] = useState<boolean>(false)
  const mapRef = useRef<any>(null)
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const overlaysRef = useRef<{ layerGroup: any } | null>(null)
  const boundsSetRef = useRef(false)
  const initialLocationReportedRef = useRef(false)
  const snapLayerRef = useRef<any>(null)
  const [isSnapping, setIsSnapping] = useState(false)

  const trailMapQuery = useQuery<TrailMapResponse>({
    enabled: !!props.trailId,
    queryFn: async () => {
      const res = await new ApiQueryService().get<TrailMapResponse>({
        endpoint: `trail-map/${props.trailId}`,
      })
      return res
    },
    queryKey: ['trail-map', props.trailId],
  })

  // Extract data directly from the query response
  const trail = trailMapQuery.data?.trail
  const obstructions = trailMapQuery.data?.obstructions || []
  const geometry = trailMapQuery.data?.geometry

  // Handle query errors
  useEffect(() => {
    if (trailMapQuery.isError) {
      setMapError(true)
    }
  }, [trailMapQuery.isError])

  // Convert GeoJSON geometry data to polylines
  useEffect(() => {
    if (!geometry || !geometry.features || geometry.features.length === 0) {
      return
    }

    const polylines: LatLng[][] = []
    geometry.features.forEach((feature: any) => {
      if (feature.geometry && feature.geometry.type === 'LineString' && feature.geometry.coordinates) {
        // Convert [lng, lat] to [lat, lng] for Leaflet
        const coords = feature.geometry.coordinates.map((coord: [number, number]) => [coord[1], coord[0]] as LatLng)
        polylines.push(coords)
      }
    })

    setWayPolylines(polylines)
  }, [geometry])

  // Filter valid obstructions
  const validObstructions = obstructions.filter((r: ObstructionReport) => {
    const lat = Number(r.gpsLat)
    const lng = Number(r.gpsLong)
    return Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0)
  })

  // Reset map if trailId changes
  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.remove()
      mapRef.current = null
      setMapInitialized(false)
      boundsSetRef.current = false
      initialLocationReportedRef.current = false
      snapLayerRef.current = null
    }
  }, [props.trailId])

  // Initialize the map ONLY after the container is ready and we have data
  useEffect(() => {
    const hasData = wayPolylines.length > 0 || validObstructions.length > 0
    const mapContainer = mapContainerRef.current
    if (!mapContainer || !hasData || typeof L === 'undefined') return
    try {
      // Only initialize once
      if (!mapInitialized && !mapRef.current) {
        mapRef.current = L.map(mapContainer)

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; OpenStreetMap contributors',
        }).addTo(mapRef.current)

        setMapInitialized(true)
      }

      if (!mapRef.current) return

      if (overlaysRef.current?.layerGroup) {
        overlaysRef.current.layerGroup.clearLayers()
      } else {
        overlaysRef.current = { layerGroup: L.layerGroup().addTo(mapRef.current) }
      }

      if (!snapLayerRef.current) {
        snapLayerRef.current = L.layerGroup().addTo(mapRef.current)
      }

      // Trail paths: Orange polylines, 4px weight
      wayPolylines.forEach((line) => {
        L.polyline(line, { color: '#ff6b35', interactive: false, weight: 4 })
          .addTo(overlaysRef.current!.layerGroup)
          .on('mouseover', function (e) {
            if (e.target && e.target._path) {
              e.target._path.style.cursor = 'grab' // keep open hand
            }
          })
      })

      // Obstruction markers: circular icons with emoji symbols and alert-level borders
      const levelToColor = (level?: string): string => {
        const l = (level || '').toLowerCase()
        if (l === 'high') return '#f44336' // red
        if (l === 'medium') return '#FF9800' // orange
        if (l === 'low') return '#2ecc71' // green
        return '#666'
      }

      validObstructions.forEach((report) => {
        const lat = Number(report.gpsLat)
        const lng = Number(report.gpsLong)
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return
        if (lat === 0 && lng === 0) return

        const color = levelToColor(report.alertLevel)
        const symbol = report.obstructionType?.symbol || '•'
        const iconHtml = `
          <div style="
          background: ${color}; 
          border: 3px solid white; 
          border-radius: 50%; 
          width: 40px; 
          height: 32px; 
          display: flex; 
          align-items: center; 
          justify-content: center; 
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
          font-size: 12px;           
          line-height: 1;            
          white-space: nowrap;       
          overflow: hidden;          
          cursor: pointer;
      ">
            <span>${symbol}</span>
          </div>`
        const icon = L.divIcon({ className: '', html: iconHtml, iconAnchor: [16, 16], iconSize: [32, 32] })

        const rawTitle = report.obstructionType?.name || 'Obstruction'
        const title = rawTitle.charAt(0).toUpperCase() + rawTitle.slice(1)
        const alert = report.alertLevel ? report.alertLevel : 'Unknown'
        let reportedAt = report.reportedAt
        try {
          reportedAt = new Date(report.reportedAt).toLocaleDateString()
        } catch {
          /* empty */
        }
        const popupHtml = `
          <div style="min-width:200px; font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;">
            <div style="font-weight:800; font-size:16px; margin-bottom:6px;color: #333333;">${report.obstructionType?.symbol} ${title}</div>
            <div style="margin-bottom:4px;"><strong style="color: #666666;font-size:14px;">Alert level: </strong><span style="background-color: ${color}; border: 1px solid ${color}; padding: 2px 6px; border-radius: 12px; color: white; font-weight: 600; text-transform: uppercase; display: inline-block;">${alert}</span></div>
            <div style="color: #666666;font-size:14px;"><strong >Reported:</strong> ${reportedAt}</div>
            <div style="margin-top:4px;color: #666666;font-size:14px;"><strong >Location:</strong><a target="_blank" rel="noopener" href="https://www.google.com/maps?q=${lat},${lng}" style="text-decoration: none;"> ${lat.toFixed(5)}, ${lng.toFixed(5)}</a></div>
          </div>`

        L.marker([lat, lng], { icon }).addTo(overlaysRef.current!.layerGroup).bindPopup(popupHtml)
      })

      // Initial view: prefer caller-supplied center (e.g. rider's GPS coords), fall back to trail bounds.
      const trailPoints: LatLng[] = wayPolylines.flat()
      if (!boundsSetRef.current) {
        if (props.initialCenter) {
          mapRef.current.setView(props.initialCenter, props.initialZoom ?? 16)
          boundsSetRef.current = true
        } else if (trailPoints.length) {
          const bounds = L.latLngBounds(trailPoints.map(([lat, lng]) => [lat, lng]))
          mapRef.current.fitBounds(bounds, { padding: [16, 16] })
          boundsSetRef.current = true
        }
      }
    } catch (error) {
      console.error('Error initializing map:', error)
      setMapError(true)
    }
  }, [wayPolylines, validObstructions, mapInitialized, mapContainerRef.current])

  // Fire onLocationSelect on pan/zoom end; snap to trail when within 50 m (picker mode)
  useEffect(() => {
    if (!mapInitialized || !mapRef.current || !props.onLocationSelect) return
    const SNAP_THRESHOLD_M = 50
    const handleMoveEnd = () => {
      const { lat, lng } = mapRef.current.getCenter()
      if (wayPolylines.length > 0) {
        const result = snapToTrail(lat, lng, wayPolylines)
        if (result && result.dist <= SNAP_THRESHOLD_M) {
          if (snapLayerRef.current) {
            snapLayerRef.current.clearLayers()
            const snapHtml = `<div style="width:14px;height:14px;border:2px solid #2ecc71;border-radius:50%;background:rgba(46,204,113,0.35);"></div>`
            const icon = L.divIcon({ className: '', html: snapHtml, iconAnchor: [7, 7], iconSize: [14, 14] })
            L.marker([result.point[0], result.point[1]], { icon }).addTo(snapLayerRef.current)
          }
          setIsSnapping(true)
          props.onLocationSelect!(result.point[0], result.point[1])
          return
        }
      }
      if (snapLayerRef.current) snapLayerRef.current.clearLayers()
      setIsSnapping(false)
      props.onLocationSelect!(lat, lng)
    }
    mapRef.current.on('moveend', handleMoveEnd)
    // Report the initial center once so the caller has a location without requiring a pan.
    if (!initialLocationReportedRef.current) {
      initialLocationReportedRef.current = true
      handleMoveEnd()
    }
    return () => {
      if (mapRef.current) mapRef.current.off('moveend', handleMoveEnd)
    }
  }, [mapInitialized, props.onLocationSelect, wayPolylines])

  // Cleanup
  useEffect(() => {
    return () => {
      if (mapRef.current) {
        mapRef.current.remove()
        mapRef.current = null
        setMapInitialized(false)
        snapLayerRef.current = null
      }
    }
  }, [])

  // Show loading spinner while fetching data
  if (trailMapQuery.isLoading) {
    return (
      <Box sx={{ alignItems: 'center', display: 'flex', height: 500, justifyContent: 'center', width: '100%' }}>
        <CircularProgress />
      </Box>
    )
  }

  // Show fallback image if error or no geometry
  if (mapError || !geometry || !geometry.features || geometry.features.length === 0) {
    return (
      <Box
        sx={{
          alignItems: 'center',
          bgcolor: 'grey.200',
          display: 'flex',
          height: 500,
          justifyContent: 'center',
          width: '100%',
        }}
      >
        {trail?.featuredImgUrl ? (
          <img
            src={trail.featuredImgUrl}
            alt={trail?.name + ' trail map'}
            style={{ background: '#eee', borderRadius: 8, maxHeight: 480, maxWidth: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Typography variant="body1" color="text.secondary">
            Map temporarily unavailable
          </Typography>
        )}
      </Box>
    )
  }

  return (
    <div style={{ height: props.height ?? 500, position: 'relative', width: '100%' }}>
      <div id="map" ref={mapContainerRef} style={{ height: '100%', width: '100%' }} />
      {props.onLocationSelect && (
        <div
          style={{
            left: '50%',
            pointerEvents: 'none',
            position: 'absolute',
            top: '50%',
            transform: 'translate(-50%, -100%)',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: isSnapping ? '#2ecc71' : '#1976d2',
              border: '3px solid white',
              borderRadius: '50% 50% 50% 0',
              boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
              height: '28px',
              transform: 'rotate(-45deg)',
              width: '28px',
            }}
          />
        </div>
      )}
    </div>
  )
}
